import { filterPersonasForVoter } from "../utils/phone.js";
import { Ava } from "../atoms/Ava.jsx";
import { CopySlug } from "../atoms/CopySlug.jsx";

/**
 * Sprint v1.28.0 (AGCY) — voter-facing Agencies directory.
 *
 * Mirrors the Directory component's layout so citizens get a consistent
 * grid-of-cards experience across Officials and Agencies. Only the copy
 * differs — framing is institutional ("agencies serving your area"),
 * the CTA says "Ask" without the civic-engagement hook, and the empty
 * state invites citizens to check back as more agencies are added.
 *
 * Geo filter is applied client-side by `filterPersonasForVoter` (same as
 * Directory), which is backend-mirrored: /api/candidates?type=agency
 * already filters by voter's country/state/lga.
 */
export function AgencyDirectory({ personas, voter, onSelect, onProfile }) {
  const visible = filterPersonasForVoter(personas, voter);

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 18px 60px" }}>
      <div style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", color: "#3b82f6", textTransform: "uppercase", marginBottom: 12 }}>
          Government Services. One Conversation Away.
        </div>
        <h1 style={{ fontSize: 36, fontWeight: 900, color: "#f1f5f9", margin: 0, fontFamily: "Georgia,serif", lineHeight: 1.2 }}>
          Talk to Your<br />
          <span style={{ background: "linear-gradient(90deg,#3b82f6,#8b5cf6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Public Agencies
          </span>
        </h1>
        {voter ? (
          <p style={{ color: "#64748b", maxWidth: 440, margin: "12px auto 0", fontSize: 14, lineHeight: 1.7 }}>
            Showing {visible.length} agenc{visible.length === 1 ? "y" : "ies"} serving{" "}
            <strong style={{ color: "#94a3b8" }}>
              {voter.flag} {[voter.lga, voter.state, voter.country].find(Boolean)}
            </strong>
          </p>
        ) : (
          <p style={{ color: "#64748b", maxWidth: 440, margin: "12px auto 0", fontSize: 14, lineHeight: 1.7 }}>
            Get answers straight from the agencies that serve you — applications,
            regulations, procedures, eligibility. No phone trees, no waiting rooms.
          </p>
        )}
      </div>

      {visible.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "#475569" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🏛️</div>
          <p style={{ fontSize: 15, marginBottom: 8, color: "#64748b" }}>
            No agencies listed for your location yet.
          </p>
          <p style={{ fontSize: 13, color: "#334155" }}>
            Check back soon as more public agencies join QuestionMe.
          </p>
        </div>
      ) : (
        <div className="qm-dir-grid">
          {visible.map(p => (
            <div key={p.id} style={{ background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 15, padding: 20, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${p.accentColor},${p.accentColor}44)` }} />
              <div style={{ display: "flex", gap: 13, alignItems: "flex-start", marginBottom: 12 }}>
                <Ava ini={p.avatar} color={p.accentColor} size={50} photoUrl={p.photoUrl} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ margin: "0 0 3px", color: "#f1f5f9", fontSize: 16, fontWeight: 800, fontFamily: "Georgia,serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name}
                  </h3>
                  <div style={{ color: "#64748b", fontSize: 12, marginBottom: 6 }}>
                    {p.role}{p.location && ` · ${p.location}`}
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: 10 }}><CopySlug slug={p.slug} color={p.accentColor} /></div>
              <p style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.55, margin: "0 0 12px", fontStyle: "italic" }}>
                "{p.tagline}"
              </p>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 14 }}>
                {p.keyPolicies?.slice(0, 3).map(pol => (
                  <span key={pol} style={{ padding: "3px 9px", background: p.accentColor + "12", color: p.accentColor, border: `1px solid ${p.accentColor}25`, borderRadius: 5, fontSize: 11, fontWeight: 600 }}>
                    {pol}
                  </span>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => onProfile(p)} style={{ flex: 1, padding: "9px 0", background: "#090e1a", border: "1px solid #1e2d45", borderRadius: 9, color: "#94a3b8", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  Profile
                </button>
                <button onClick={() => onSelect(p)} style={{ flex: 2, padding: "9px 0", background: `${p.accentColor}18`, border: `1px solid ${p.accentColor}44`, borderRadius: 9, color: p.accentColor, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  💬 Ask the Agency
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AgencyDirectory;
